<script setup lang="ts">

import { CDN } from '@/models/cdn.ts'
import ActionButton from '@/components/Elements/Buttons/ActionButton.vue'
import { formatShort } from '@/models/formatNumber.ts'
import { useAssetStore } from '@/stores/assetStore.ts'
import { useGameStore } from '@/stores/gameStore.ts'
import { usePlayerOreStore } from '@/stores/playerOreStore.ts'
import Backend from '@/models/backend.ts'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()

/**
 * Props
 */
const props = defineProps({
  buildingCode: {
    type: String,
    required: true
  },
  planetId: {
    type: Number,
    required: true
  },
  productionObjectId: {
    type: Number,
    required: true
  }
})

/**
 * Stores / Models
 */
const assetStore = useAssetStore()
const gameStore = useGameStore()
const playerOreStore = usePlayerOreStore()
const backend = new Backend()

/**
 * Computed
 */
const buildingData = computed(() => {
  return assetStore.findBuildingByCode(props.buildingCode)
})

const canPurchaseWithOre = computed(() => {
  if (!buildingData.value) return false
  return !!buildingData.value.purchaseWithOre &&
         !!buildingData.value.purchaseWithOre.oreCode &&
         buildingData.value.purchaseWithOre.quantity > 0
})

const oreData = computed(() => {
  if (!canPurchaseWithOre.value || !buildingData.value.purchaseWithOre) return null

  const oreCode = buildingData.value.purchaseWithOre.oreCode
  const allOres = Object.values(assetStore.ores)
  const ore = allOres.find(o => o.code === oreCode)

  return ore || null
})

const oreId = computed(() => {
  return oreData.value?.id || null
})

const quantity = computed(() => {
  const baseQuantity = buildingData.value?.purchaseWithOre?.quantity || 0
  const multiplier = gameStore.player?.buyingBuildingWithOreMultiplier || 1
  return Math.ceil(baseQuantity * multiplier)
})

const tooltipText = computed(() => {
  if (!oreData.value) return t('planets.purchaseWithOre.tooltip')
  return t('planets.purchaseWithOre.tooltipWithDetails', {
    oreName: oreData.value.oreName,
    quantity: formatShort(quantity.value)
  })
})

const playerHasEnoughOre = computed(() => {
  if (!canPurchaseWithOre.value || !buildingData.value?.purchaseWithOre) return false

  const oreCode = buildingData.value.purchaseWithOre.oreCode
  const playerOreQuantity = playerOreStore.getOreStored(oreCode)

  return playerOreQuantity >= quantity.value
})

/**
 * Methods
 */
const handleClick = () => {
  if (!canPurchaseWithOre.value) return

  const oreName = oreData.value?.oreName || 'ore'
  const buildingName = buildingData.value ? t(`buildings.buildings.${buildingData.value.buildingCode}.name`) : 'building'

  gameStore.modal2Type = 'confirmationDelete'
  gameStore.modal2Data = {
    title: t('common.confirmation.areYouSure'),
    message: t('planets.purchaseWithOre.confirmMessage', {
      quantity: formatShort(quantity.value),
      oreName: oreName,
      buildingName: buildingName
    }),
    actionText: t('common.actions.yes'),
    cancelText: t('common.actions.no'),
    action: async () => {
      try {
        await backend.buyBuildingWithOre(props.planetId, props.buildingCode)
        gameStore.setTinySuccessToastMessage(t('planets.purchaseWithOre.success'))
      } catch (error) {
        gameStore.setTinyErrorToastMessage(t('planets.purchaseWithOre.failed'))
      }
    }
  }
}

</script>

<template>
  <ActionButton v-if="canPurchaseWithOre && oreId && playerHasEnoughOre">
    <div
      class="flex items-center cursor-pointer"
      @click="handleClick"
      :title="tooltipText"
    >
      <img
        :src="CDN.ore(oreId, 64)"
        class="inline"
        style="width: 20px; height: 20px;"
        :alt="`Ore ${oreId}`"
      />
      <span class="text-slate-300 ps-2">{{ formatShort(quantity) }}</span>
    </div>
  </ActionButton>
</template>

<style scoped lang="scss">

</style>